import type { PaginatedApiParams } from '~/common/types/api.type';

/**
 * 구매 상태
 */
export enum PurchaseStatus {
  /** 대기 중 */
  PENDING = 'PENDING',
  /** 현금 결제 대기 중 */
  CASH_PENDING = 'CASH_PENDING',
  /** 완료 */
  COMPLETED = 'COMPLETED',
  /** 실패 */
  FAILED = 'FAILED',
  /** 취소됨 */
  CANCELLED = 'CANCELLED',
  /** 환불됨 */
  REFUNDED = 'REFUNDED',
}

/**
 * 결제 유형
 */
export enum PaymentType {
  /** 현금 */
  CASH = 'CASH',
  /** 토스 */
  TOSS = 'TOSS',
}

/**
 * 구매 사용자 정보
 */
export interface PurchaseUser {
  /** 사용자 ID */
  id: number;
  /** 사용자 계정 */
  userId: string;
  /** 사용자명 */
  name: string;
  /** 전화번호 */
  phoneNumber: string;
  /** 추천코드 */
  referralCode: string;
}

/**
 * 구매 상품 정보
 */
export interface PurchaseProduct {
  /** 상품 ID */
  id: number;
  /** 상품 코드 */
  code: string;
  /** 상품명 */
  name: string;
  /** 상품 가격 */
  amount: number;
  /** 부가세 */
  vat?: number;
  /** 만료기간(일) */
  expirationDays: number;
}

/**
 * 구매 엔티티
 */
export interface Purchase {
  /** 구매 ID */
  id: number;
  /** 결제 금액 */
  amount: number;
  /** 만료일시 */
  expiredAt: string;
  /** 구매 상태 */
  status: PurchaseStatus;
  /** 사용자 정보 */
  user: PurchaseUser;
  /** 상품 정보 */
  product: PurchaseProduct;
  /** 결제 유형 */
  paymentType: PaymentType;
  /** 결제 수단 (토스) */
  paymentMethod: string | null;
  /** 결제 키 (토스) */
  paymentKey: string | null;
  /** 주문 ID (토스) */
  orderId: string | null;
  /** 결제 데이터 (토스) */
  paymentData: Record<string, unknown> | null;
  /** 입금자명 */
  depositorName: string | null;
  /** 관리자 메모 */
  adminMemo: string | null;
  /** 구매일시 */
  createdAt: string;
  /** 수정일시 */
  updatedAt: string;
}

/**
 * 구매 목록 정렬 키
 */
export type PurchaseOrderKey = 'id' | 'amount' | 'status' | 'createdAt' | 'updatedAt';

/**
 * 구매 목록 조회 파라미터
 */
export interface PurchaseListParams extends PaginatedApiParams<PurchaseOrderKey> {
  /** 사용자 ID로 필터링 */
  userId?: number;
  /** 결제 상태 필터 */
  status?: PurchaseStatus;
  /** 사용자명 검색 (부분 일치) */
  userName?: string;
  /** 사용자 계정 검색 (부분 일치) */
  userAccount?: string;
  /** 상품 코드로 필터링 */
  productCode?: string;
  /** 구매일 필터 (YYYY-MM-DD) */
  purchaseDate?: string;
}

/**
 * 현금 결제 승인/거부 응답
 */
export interface CashPaymentActionResponse {
  success: boolean;
  message: string;
  purchase?: Purchase;
}

/**
 * 구매 생성 요청
 * @deprecated Admin에서 구매 생성 API 미사용
 */
export interface PurchaseCreateRequest {
  /** 사용자 ID */
  userId: number;
  /** 상품 ID */
  productId: number;
  /** 결제 유형 */
  paymentType: PaymentType;
}

/**
 * 구매 통계 조회 파라미터
 */
export interface PurchaseStatisticsParams {
  /** 매장 ID */
  storeId?: number;
  /** 시작일 (YYYY-MM-DD) */
  startDate?: string;
  /** 종료일 (YYYY-MM-DD) */
  endDate?: string;
  /** 상품 코드 */
  productCode?: string;
}

/**
 * 구매 상태 변경 요청
 */
export interface PurchaseStatusRequest {
  /** 변경할 상태 */
  status: 'completed' | 'cancelled' | 'refunded';
  /** 사유 */
  reason?: string;
}

/**
 * 구매 통계 응답
 */
export interface PurchaseStatistics {
  /** 총 구매 건수 */
  totalCount: number;
  /** 총 구매 금액 */
  totalAmount: number;
  /** 상태별 통계 */
  byStatus: Record<string, { count: number; amount: number }>;
  /** 상품별 통계 */
  byProduct: Array<{
    productId: number;
    productCode: string;
    productName: string;
    count: number;
    amount: number;
  }>;
}
